import { FC, useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { useBreakpoint } from '@contexts/breakpoints';
import { callService } from '@api/caller';
import { SearchViewModel, WebUrlType } from '@api/index';
import { FlexibleDiv } from '@components/formStyles';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import {
  ItemText,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon from '@components/utils/CustomIcon';
import makeBold from '@utils/makeBold';
import AutoComplete, { Option } from '@shared/AutoComplete';
import { MapPin2Line } from '@assets/icons';
import { BookerSearchViewModel } from '@components/views/landing/components/booker/common/BookerSearchDrawer';
import { PastSearches } from '@shared';

import {
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  ItemErrorText,
  OptionWrapper,
  SelectWrapper,
} from './styled';

export type HotelTypes = SearchViewModel | null;

interface IHotelAutoComplete {
  label: string;
  onChange?: (data: HotelTypes) => void;
  history?: BookerSearchViewModel[];
  clearPastHotelSearch?: () => void;
  removePastHotelSearch?: (item: BookerSearchViewModel) => void;
  isLocationError: boolean;
  setIsLocationError: (value: boolean) => void;
  activeLocation?: HotelTypes;
}

const HotelAutoComplete: FC<IHotelAutoComplete> = ({
  label,
  onChange: pOnChange = (v) => v,
  history,
  clearPastHotelSearch,
  removePastHotelSearch,
  isLocationError,
  setIsLocationError,
  activeLocation,
}) => {
  const { xs } = useBreakpoint();

  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState<Array<SearchViewModel> | []>([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isHotelResultsLoading, setIsHotelResultsLoading] = useState(false);
  const [isHotelDataFetched, setIsHotelDataFetched] = useState(false);

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (value === '') {
      pOnChange(null);
    }

    setIsHotelDataFetched(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsHotelResultsLoading(true);
      const hotels = await callService('SearchesService', 'searchesHotel', {
        s: s,
      });
      setData(hotels);
    } catch (error) {
      setData([]);
    } finally {
      setIsHotelResultsLoading(false);
      setIsHotelDataFetched(true);
    }
  };

  useEffect(() => {
    if (activeLocation) {
      setInputValue(activeLocation?.text || '');
    }
  }, [activeLocation]);

  const searchHotel = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue = activeLocation?.text !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchHotel(inputValue);
    } else {
      setData([]);
    }
  }, [inputValue]);

  useEffect(() => {
    if (activeLocation) {
      setInputValue(activeLocation?.text || '');
    }
  }, [activeLocation]);

  const handleFocus = () => {
    setIsFocus(true);
    isLocationError && setIsLocationError(false);
  };

  const handleBlur = () => {
    setIsFocus(false);
    isLocationError && setIsLocationError(false);
  };

  const selectValue = (option: SearchViewModel) => {
    pOnChange(option);
    setInputValue(option?.text || '');
  };

  const isShowItemTextError =
    isHotelDataFetched &&
    !isHotelResultsLoading &&
    inputValue.length > 2 &&
    data &&
    Array.isArray(data) &&
    !data.length;

  const showPastSearch = Array.isArray(history)
    ? history?.length > 0 && inputValue.length === 0
    : false;

  return (
    <SelectWrapper>
      <AutoComplete
        onSearch={handleSearch}
        onFocus={handleFocus}
        onBlur={handleBlur}
        allowClear
        isFocus={isFocus}
        isError={isLocationError}
        value={inputValue}
        dropdownStyle={{
          minWidth: xs || xs !== undefined ? 'auto' : '400px',
        }}
        label={label}
        icon={MapPin2Line}
      >
        <>
          {showPastSearch ? (
            <>
              <Option className='pastSearchesTitleWrapper'>
                <PastSearches.Header
                  label='Son Aramalar'
                  handleClear={() => clearPastHotelSearch?.()}
                />
              </Option>
              {history?.map((item) => (
                <Option key={item?.id}>
                  <PastSearches.Item
                    label={item.text}
                    type={item?.type}
                    onClick={() =>
                      selectValue({
                        id: item?.id,
                        text: item?.text,
                        type: item?.type as WebUrlType,
                        url: item?.url,
                      })
                    }
                    handleRemove={() => removePastHotelSearch?.(item)}
                  />
                </Option>
              ))}
            </>
          ) : null}
          {data && Array.isArray(data) && data.length ? (
            data.map((option) => (
              <Option key={`${option.id}-${option.text}`}>
                <OptionWrapper onClick={() => selectValue(option)}>
                  <CustomIcon
                    width={24}
                    height={24}
                    fontSize={24}
                    paletteColor='grey800'
                    icon={getLocationIcon(option?.type || '')}
                  />
                  <FlexibleDiv right={12} />
                  <ItemTextWrapper>
                    <ItemText
                      dangerouslySetInnerHTML={{
                        __html: makeBold(option.text || '', inputValue),
                      }}
                    />
                  </ItemTextWrapper>
                </OptionWrapper>
              </Option>
            ))
          ) : isShowItemTextError ? (
            <Option>
              <OptionWrapper>
                <ItemErrorText>
                  {!xs || xs === undefined ? (
                    <span>Aradığınız kriterlere uygun sonuç bulunamadı.</span>
                  ) : (
                    <span>
                      Aradığınız kriterlere uygun <br />
                      sonuç bulunamadı.
                    </span>
                  )}
                </ItemErrorText>
              </OptionWrapper>
            </Option>
          ) : null}
        </>
      </AutoComplete>
      {isLocationError && (
        <ErrorMessageContainer>
          <Arrow />
          <ErrorMessage>Şehir yada otel adı seçiniz</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </SelectWrapper>
  );
};

export default HotelAutoComplete;
